import { tokenize } from './trackedLinkRepair';
import { descendants, isWord, parseXml, WORD_NS } from './xml';

/** An exhaustive, same-revision read of the live public Document API. */
export interface TableRecoveryProof {
  paragraphTexts: readonly string[];
  tableCount: 0;
  trackedChangeCount: 0;
}

/**
 * The audit proved a removed one-cell heading can leave three closing tags.
 * The same bytes could also mean missing opening tags: syntax alone is not
 * enough. Only a complete live-model witness may authorize this correction.
 */
export function repairOrphanTable(xml: string, proof: TableRecoveryProof): string | null {
  if (proof.tableCount !== 0 || proof.trackedChangeCount !== 0 || !Array.isArray(proof.paragraphTexts)
    || !proof.paragraphTexts.every((text) => typeof text === 'string')) return null;
  const tableTokens = tokenize(xml).filter((token) => ['w:tc', 'w:tr', 'w:tbl'].includes(token.name));
  if (tableTokens.length !== 3 || tableTokens.some((token) => token.kind !== 'close')
    || tableTokens.map((token) => token.name).join(',') !== 'w:tc,w:tr,w:tbl') return null;
  for (let index = 1; index < tableTokens.length; index++) {
    if (xml.slice(tableTokens[index - 1]!.end, tableTokens[index]!.start).trim()) return null;
  }
  let output = xml;
  for (const token of [...tableTokens].reverse()) output = output.slice(0, token.start) + output.slice(token.end);
  let root;
  try { root = parseXml(output); } catch { return null; }
  if (root.uri !== WORD_NS || root.local !== 'document') return null;
  const body = root.children.find((node) => isWord(node, 'body'));
  if (!body || body.children.some((node) => !isWord(node, 'p') && !isWord(node, 'sectPr'))) return null;
  // No revision, table or nested paragraph may be hidden from the witness.
  const nodes = [...descendants(root)];
  // Public revision lists can omit property changes. XML must independently
  // agree with the zero-revision witness before this narrow repair is allowed.
  if (nodes.some((node) => isWord(node, node.local) && (
    ['tbl', 'tc', 'tr', 'del', 'ins', 'cellIns', 'cellDel', 'cellMerge'].includes(node.local)
    || node.local.endsWith('Change') || node.local.startsWith('move') || node.local.startsWith('customXml')
  ))) return null;
  const paragraphs = body.children.filter((node) => isWord(node, 'p'));
  if (nodes.filter((node) => isWord(node, 'p')).length !== paragraphs.length || paragraphs.length !== proof.paragraphTexts.length) return null;
  const texts = paragraphs.map((paragraph) => [...descendants(paragraph)].map((node) =>
    isWord(node, 't') ? node.text : isWord(node, 'tab') ? '\t' : isWord(node, 'br') || isWord(node, 'cr') ? '\n' : '').join(''));
  if (texts.some((text, index) => text !== proof.paragraphTexts[index])) return null;
  return output;
}
